import React from 'react';
import styled from 'styled-components';
import { useTheme, withThemeProvider } from '@narvar/nth-hook-theme';

/**
 * NOTE: This is pretty much just `nth-checkbox` copied over.
 *       The differences are as follows:
 *       1) Converted to Typescript
 *       2) Component now forwards ref to input element to work
 *          with react-hook-form.
 *       3) Added `onBlur` for react-hook-form
 */

const checkD = 'M12.78 29.1l-9.56-9.55L0 22.77l12.78 12.78L40 8.22 36.88 5';

const Svg = styled.svg<{ checkStyle?: React.CSSProperties }>`
  display: block;
  width: 100%;
  fill: ${(props) => props.checkStyle?.fill || '#ffffff'};
  vertical-align: ${(props) => props.checkStyle?.verticalAlign || 'initial'};
`;

const Check: React.FunctionComponent<{ checkStyle?: React.CSSProperties }> = ({
  checkStyle = {},
}) => (
  <Svg checkStyle={checkStyle} viewBox="0 0 40 40">
    <path d={checkD} />
  </Svg>
);

const Container = styled.div<{ s?: React.CSSProperties }>`
  width: ${(props) => props.s?.width || 'auto'};
  height: ${(props) => props.s?.height || 'auto'};
  display: ${(props) => props.s?.display || 'block'};
`;

const Label = styled.label`
  cursor: pointer;
`;

const LabelTable = styled.div`
  display: table;
`;

const LabelBoxCell = styled.div<{ labelBoxCellStyle?: React.CSSProperties }>(
  ({ labelBoxCellStyle }) => ({
    display: 'table-cell',
    verticalAlign: 'top',
    ...labelBoxCellStyle,
  }),
);

const LabelTextCell = styled.div<{ labelTextCellStyle?: React.CSSProperties }>`
  display: table-cell;
  vertical-align: ${(props) =>
    props.labelTextCellStyle?.verticalAlign || 'top'};
  padding-left: ${(props) => props.labelTextCellStyle?.paddingLeft || '.5rem'};
`;

const CheckContainer = styled.div<{
  boxStyle?: React.CSSProperties;
  checked?: boolean;
  errorStyle?: React.CSSProperties;
}>`
  background: ${(props) =>
    `${
      props.checked ? props.boxStyle?.background || '#0874ba' : 'transparent'
    }`};
  border-width: ${(props) => props.boxStyle?.borderWidth || '1px'};
  border-radius: ${(props) => props.boxStyle?.borderRadius || '2px'};
  border-style: solid;
  border-color: ${(props) =>
    props.errorStyle?.borderColor || props.boxStyle?.borderColor || '#034977'};
  width: ${(props) => `calc(${props.boxStyle?.width || '1.2rem'} * 0.8)`};
  height: ${(props) => `calc(${props.boxStyle?.height || '1.2rem'} * 0.8)`};
  padding: ${(props) => `calc(${props.boxStyle?.width || '.15rem'} * 0.1)`};
  display: inline-block;
  vertical-align: ${(props) => props.boxStyle?.verticalAlign || 'top'};
`;

const StyledInput = styled.input`
  display: none;
`;

interface CheckboxProps {
  children?: React.ReactNode;
  id: string;
  ariaDescribedby?: string;
  error?: boolean;
  name: string;
  checked?: boolean;
  value?: string;
  containerStyle?: React.CSSProperties;
  boxStyle?: React.CSSProperties;
  labelBoxCellStyle?: React.CSSProperties;
  labelTextCellStyle?: React.CSSProperties;
  checkStyle?: React.CSSProperties;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event?: React.FocusEvent<HTMLInputElement>) => void;
}

const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      children = null,
      ariaDescribedby,
      id,
      name,
      value,
      error,
      onChange,
      onBlur,
      checked = false,
      containerStyle = {
        width: undefined,
        height: undefined,
        display: undefined,
      },
      boxStyle = {},
      labelBoxCellStyle = {},
      labelTextCellStyle = {},
      checkStyle = {},
    },
    ref,
  ) => {
    const { captiontextErrorNone: errorTextStyle } = useTheme();

    const errorColor = (errorTextStyle as { color?: string } | undefined)
      ?.color;

    return (
      <Container s={containerStyle}>
        <Label htmlFor={id}>
          <LabelTable>
            <LabelBoxCell labelBoxCellStyle={labelBoxCellStyle}>
              <CheckContainer
                boxStyle={boxStyle}
                checked={checked}
                errorStyle={error ? { borderColor: errorColor } : undefined}
              >
                {checked && <Check checkStyle={checkStyle} />}
              </CheckContainer>
            </LabelBoxCell>
            {children ? (
              <LabelTextCell labelTextCellStyle={labelTextCellStyle}>
                {children}
              </LabelTextCell>
            ) : null}
          </LabelTable>
        </Label>
        <StyledInput
          id={id}
          name={name}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          type="checkbox"
          ref={ref}
          aria-describedby={ariaDescribedby}
        />
      </Container>
    );
  },
);

export default withThemeProvider(Checkbox, {
  themeTokens: ['captiontextErrorNone'],
});
